import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Spin, Alert, Tag, Space, Card, Typography } from 'antd';
import Layout from '../../components/Layout';
import { httpGet, httpPost, customBaseUrl } from '../../store/http';
import { showLoader, hideLoader } from '../../utils/loader';
import { NotificationManager } from 'react-notifications';
import Swal from 'sweetalert2';
import moment from 'moment';

const { Title } = Typography;

const CategoryChangeRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPendingRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await httpGet(`${customBaseUrl.authUrl}/admin/users/category-changes/pending`);
      if (res.success || res.status) {
        setRequests(res.data || []);
      } else {
        setError(res.message || 'Failed to fetch pending requests.');
      }
    } catch (err) {
      setError('An error occurred while fetching requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingRequests();
  }, [fetchPendingRequests]);

  const handleAction = async (action, requestId) => {
    const { value: comment } = await Swal.fire({
      title: `Confirm ${action}`,
      text: `Are you sure you want to ${action.toLowerCase()} this category change request?`,
      input: 'textarea',
      inputLabel: 'Admin Remarks',
      inputPlaceholder: 'Enter your remarks here...',
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value && action === 'REJECT') {
          return 'You need to provide a reason for rejection!'
        }
      }
    });

    if (comment !== undefined) {
      showLoader();
      try {
        const endpoint = `${customBaseUrl.authUrl}/admin/users/category-changes/${requestId}/${action.toLowerCase()}`;
        // Using httpPost since the request body might be needed or standard for these actions
        const res = await httpPost(endpoint, { remarks: comment });
        
        if (res.success || res.status) {
          NotificationManager.success(`Request ${action.toLowerCase()}ed successfully!`);
          fetchPendingRequests(); // Refresh the list
        } else {
          NotificationManager.error(res.message || `Failed to ${action.toLowerCase()} request.`);
        }
      } catch (err) {
        NotificationManager.error(`An error occurred.`);
      } finally {
        hideLoader();
      }
    }
  };

  const columns = [
    { 
      title: 'Request ID', 
      dataIndex: 'requestId', 
      key: 'requestId',
      render: (text) => <Typography.Text copyable>{text}</Typography.Text>
    },
    { 
      title: 'Maker (Requester)', 
      key: 'maker',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{record.makerName || 'N/A'}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: '11px' }}>ID: {record.makerId}</Typography.Text>
        </Space>
      )
    },
    { 
      title: 'Current', 
      dataIndex: 'fromCategory', 
      key: 'fromCategory',
      render: (cat) => <Tag color="blue">{cat || 'NONE'}</Tag>
    },
    { 
      title: 'Target', 
      dataIndex: 'toCategory', 
      key: 'toCategory',
      render: (cat) => <Tag color="purple">{cat}</Tag>
    },
    { 
      title: 'Maker Remarks', 
      dataIndex: 'makerRemarks', 
      key: 'makerRemarks',
      ellipsis: true
    },
    { 
      title: 'Date Requested', 
      dataIndex: 'createdAt', 
      key: 'createdAt', 
      render: (text) => moment(text).format('LLL') 
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Space>
          <Button 
            type="primary" 
            size="small" 
            style={{ background: '#52c41a', borderColor: '#52c41a' }}
            onClick={() => handleAction('APPROVE', record.requestId)}
          >
            Approve
          </Button>
          <Button 
            type="primary" 
            danger 
            size="small" 
            onClick={() => handleAction('REJECT', record.requestId)}
          >
            Reject
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Layout>
      <div style={{ padding: '24px' }}>
        <Card bordered={false} className="premium-card">
          <div style={{ marginBottom: '24px' }}>
            <Title level={4}>Category Change Approval (Checker)</Title>
            <Typography.Text type="secondary">Review and action pending customer category change requests.</Typography.Text>
          </div>

          {error && <Alert message="Error" description={error} type="error" showIcon style={{ marginBottom: 16 }} />}

          <Table 
            dataSource={requests} 
            columns={columns} 
            rowKey="id" 
            loading={loading}
            pagination={{ pageSize: 10 }}
            className="premium-table"
          />
        </Card>
      </div>
    </Layout>
  );
};

export default CategoryChangeRequests;
